import type { ApiRoutes, RouteKey } from '@mailpilot/shared';
import { bridge } from './desktop';

// ---------- Errors ----------

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
  get isPrerequisite(): boolean {
    return this.code === 'prerequisite_missing' || this.status === 412;
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

// ---------- Auth / base URL ----------

type AuthListener = () => void;
const unauthListeners = new Set<AuthListener>();
/** Called whenever the API reports the session is missing or revoked. */
export function onUnauthenticated(cb: AuthListener): () => void {
  unauthListeners.add(cb);
  return () => unauthListeners.delete(cb);
}
function emitUnauthenticated() {
  unauthListeners.forEach((cb) => cb());
}

let baseUrlPromise: Promise<string> | null = null;
export function baseUrl(): Promise<string> {
  if (!baseUrlPromise) {
    const b = bridge();
    baseUrlPromise = b
      ? b.getServerUrl().then((u) => u.replace(/\/+$/, '')).catch(() => '')
      : Promise.resolve('');
  }
  return baseUrlPromise;
}
export function resetBaseUrl() {
  baseUrlPromise = null;
}

export async function authToken(): Promise<string | null> {
  const b = bridge();
  if (!b) return null;
  try {
    return await b.getToken();
  } catch {
    return null;
  }
}

export async function storeToken(token: string | null): Promise<void> {
  const b = bridge();
  if (b) await b.setToken(token);
}

async function authHeaders(): Promise<Record<string, string>> {
  const t = await authToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Absolute URL for a path (used for EventSource, images, downloads). */
export async function absoluteUrl(path: string): Promise<string> {
  return `${await baseUrl()}${path}`;
}

// ---------- Typed route helpers ----------

type Def<K extends RouteKey> = ApiRoutes[K];
export type BodyOf<K extends RouteKey> = Def<K> extends { body: infer B } ? B : undefined;
export type QueryOf<K extends RouteKey> = Def<K> extends { query: infer Q } ? Q : undefined;
export type ResOf<K extends RouteKey> = Def<K> extends { res: infer R } ? R : never;

type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;
type PathOf<K extends RouteKey> = K extends `${string} ${infer P}` ? P : never;
type Params<K extends RouteKey> = [ParamNames<PathOf<K>>] extends [never] ? undefined : Record<ParamNames<PathOf<K>>, string>;

export type CallOptions<K extends RouteKey> = {
  params?: Params<K>;
  query?: QueryOf<K>;
  body?: BodyOf<K>;
  signal?: AbortSignal;
};

function buildPath(template: string, params?: Record<string, string>, query?: object): string {
  let path = template.replace(/:([A-Za-z_]+)/g, (_, name: string) => {
    const v = params?.[name];
    if (v === undefined) throw new Error(`Missing route param ${name}`);
    return encodeURIComponent(v);
  });
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) path += `?${s}`;
  }
  return path;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = `http_${res.status}`;
  let message = res.statusText || 'Request failed';
  let details: unknown;
  try {
    const j = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    if (j?.error) {
      code = j.error.code ?? code;
      message = j.error.message ?? message;
      details = j.error.details;
    }
  } catch {
    /* not json */
  }
  return new ApiError(res.status, code, message, details);
}

export async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = `${await baseUrl()}${path}`;
  const headers = { ...(await authHeaders()), ...(init.headers as Record<string, string> | undefined) };
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers, credentials: 'include' });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network_error', navigator.onLine ? 'Cannot reach the MailPilot server.' : 'You are offline.');
  }
  if (!res.ok) {
    const err = await parseError(res);
    if (res.status === 401 && !path.startsWith('/api/auth/login') && !path.startsWith('/api/auth/signup')) emitUnauthenticated();
    throw err;
  }
  return res;
}

/** Call a contract route: api('GET /api/threads/:id', { params: { id } }) */
export async function api<K extends RouteKey>(key: K, opts: CallOptions<K> = {}): Promise<ResOf<K>> {
  const space = key.indexOf(' ');
  const method = key.slice(0, space);
  const template = key.slice(space + 1);
  const path = buildPath(template, opts.params as Record<string, string> | undefined, opts.query as object | undefined);
  const init: RequestInit = { method, signal: opts.signal };
  if (opts.body !== undefined && method !== 'GET') {
    if (opts.body instanceof FormData) {
      init.body = opts.body;
    } else {
      init.body = JSON.stringify(opts.body);
      init.headers = { 'Content-Type': 'application/json' };
    }
  } else if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
    init.body = '{}';
    init.headers = { 'Content-Type': 'application/json' };
  }
  const res = await rawFetch(path, init);
  if (res.status === 204) return { ok: true } as ResOf<K>;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await res.json()) as ResOf<K>;
  if (ct.startsWith('text/')) return (await res.text()) as ResOf<K>;
  return (await res.blob()) as ResOf<K>;
}

/** Fetch a binary resource (preview/download) with auth, returning a Blob. */
export async function fetchBlob(path: string): Promise<Blob> {
  const res = await rawFetch(path);
  return res.blob();
}

/** Download a server resource to the user's device. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await rawFetch(path);
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const blob = await res.blob();
  saveBlob(blob, name);
}

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
